import PropTypes from "prop-types";
import React, { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  getDiscountPrice,
  getVariantWeightPrice,
} from "../../../helpers/product";
import { useTranslation } from "react-i18next";
import urls from "../../../urls";
import { formatPrice, formatWeight } from "../../../helpers/formatters";
import {
  CURRENCY,
  DELIVERY_OPTIONS,
  MINIMUM_ORDER_AMOUNT,
} from "../../../constants/DeliveryConstants";
import { PRODUCT_PLACEHOLDER_SRC } from "../../../constants/ProductConstants";
import CtaLink from "../../cta/CtaLink";
import { setProductAnalyticsEvent } from "../../../helpers/analytics";
import { ANALYTICS_REMOVE_FROM_CART } from "../../../constants/AnalyticsConstants";
import LazyImage from "../../image/LazyImage";
import { trackProductRemoved } from "../../../helpers/posthogAnalytics";
import { useDeliveryAddress } from "../../../contexts/DeliveryAddressProvider";
import {
  calculateRemainingForFreeShipping,
  calculateRemainingForMinimumOrder,
  calculateShippingCost,
} from "../../../helpers/calculators";
import { colors } from "../../../utils/theme";

const MenuCart = ({ cartData, deleteFromCart, reference }) => {
  const { t } = useTranslation();
  const { values: deliveryValues, openModal: openDeliveryModal } =
    useDeliveryAddress();

  const { cartTotalPrice, totalItemsPrice, totalGiftcardsPrice } =
    useMemo(() => {
      let total = 0;
      let items = 0;
      let giftcards = 0;
      cartData?.forEach((single) => {
        const price = getVariantWeightPrice(single);
        const discounted = getDiscountPrice(price, single.discount);
        const finalPrice =
          (discounted != null ? discounted : price) * single.quantity;
        total += finalPrice;
        if (single.isGiftCard) giftcards += finalPrice;
        else items += finalPrice;
      });
      return {
        cartTotalPrice: total,
        totalItemsPrice: items,
        totalGiftcardsPrice: giftcards,
      };
    }, [cartData]);

  // Net als op de winkelwagenpagina: de bezorgmethode valt pas in de checkout,
  // dus rekenen we hier met BEZORGEN en vertellen we erbij dat afhalen gratis
  // is. Zie de toelichting in `pages/other/Cart.js`.
  const shippingCost = useMemo(() => {
    return calculateShippingCost(
      totalItemsPrice,
      totalGiftcardsPrice,
      deliveryValues,
      DELIVERY_OPTIONS.SEND,
    );
  }, [deliveryValues, totalItemsPrice, totalGiftcardsPrice]);

  const remainingForMinimum = useMemo(
    () => calculateRemainingForMinimumOrder(totalItemsPrice),
    [totalItemsPrice],
  );

  const formattedShipping =
    shippingCost === null
      ? t("choose_shipping_method")
      : shippingCost === 0
        ? t("free_shipping")
        : CURRENCY + formatPrice(shippingCost);

  const remainingForFree = useMemo(() => {
    // De drempel hangt van de regio af (EUR 100 in West-Friesland, EUR 150
    // daarbuiten); zonder postcode is er nog geen bedrag en dus geen boodschap.
    return calculateRemainingForFreeShipping(
      shippingCost,
      totalItemsPrice,
      deliveryValues,
    );
  }, [shippingCost, totalItemsPrice, deliveryValues]);

  return (
    <div className="shopping-cart-content" ref={reference}>
      {cartData && cartData.length > 0 ? (
        <Fragment>
          <ul>
            {cartData.map((single, key) => {
              const price = getVariantWeightPrice(single);
              const discountedPrice = getDiscountPrice(price, single.discount);

              return (
                <li className="single-shopping-cart" key={key}>
                  <div className="shopping-cart-img">
                    <Link
                      to={
                        single.isGiftCard
                          ? urls.cadeaubon
                          : urls.product + single.url
                      }
                    >
                      <LazyImage
                        alt={single.name}
                        src={
                          !!single.image
                            ? single.image[0]
                            : PRODUCT_PLACEHOLDER_SRC
                        }
                        className="img-fluid"
                        rounded
                      />
                    </Link>
                  </div>
                  <div className="shopping-cart-title">
                    <h4>
                      <Link
                        to={
                          single.isGiftCard
                            ? urls.cadeaubon
                            : urls.product + single.url
                        }
                      >
                        {" "}
                        {single.name}{" "}
                      </Link>
                    </h4>
                    <h6>
                      {t("quantity")}: {single.quantity}
                    </h6>
                    {single.variation && single.variation.weight && (
                      <h6>
                        {t("weight")}: {formatWeight(single.variation.weight)}
                        {t("gram_unit")}
                      </h6>
                    )}
                    {single.type && (
                      <h6>
                        {t("type")}: {single.type}
                      </h6>
                    )}
                    <span>
                      {discountedPrice !== null
                        ? CURRENCY + formatPrice(discountedPrice)
                        : CURRENCY + formatPrice(price)}
                    </span>
                  </div>
                  <div className="shopping-cart-delete">
                    <button
                      onClick={() => {
                        deleteFromCart(single);
                        setProductAnalyticsEvent(
                          ANALYTICS_REMOVE_FROM_CART,
                          single,
                          single.quantity,
                        );
                        trackProductRemoved(single, single.quantity);
                      }}
                    >
                      <i
                        className="fa fa-times-circle"
                        id={"delete-cart-item"}
                      />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <p
            className="text-muted"
            style={{ fontSize: 12, lineHeight: 1.2, marginBottom: 12 }}
          >
            {t("we_ship_frozen")}
          </p>
          <div className="shopping-cart-total">
            <h6 style={{ margin: 0 }}>
              {shippingCost === 0 ? (
                `✅ ${formattedShipping}`
              ) : (
                <>
                  {t("shipping_cost_when_delivered")}:{" "}
                  {shippingCost === null ? (
                    <span
                      style={{
                        color: colors.clickable_text,
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={async () => {
                        await openDeliveryModal();
                      }}
                    >
                      {t("enter_zip_code")}
                    </span>
                  ) : (
                    <span className="shop-total">{formattedShipping}</span>
                  )}
                </>
              )}
            </h6>
            {remainingForMinimum > 0 && (
              <p
                className="text-muted"
                style={{ fontSize: 12, lineHeight: 1.2, marginTop: 6 }}
              >
                {t("cart_below_delivery_minimum", {
                  min_amount: MINIMUM_ORDER_AMOUNT,
                  value: formatPrice(remainingForMinimum),
                })}
              </p>
            )}
            {remainingForMinimum === 0 &&
              shippingCost > 0 &&
              remainingForFree > 0 && (
                <p
                  className="text-muted"
                  style={{ fontSize: 12, lineHeight: 1.2, marginTop: 6 }}
                >
                  {t("remaining_for_free_shipping", {
                    value: formatPrice(remainingForFree),
                  })}
                </p>
              )}
            <h4>
              {t("total")}:{" "}
              <span className="shop-total">
                {CURRENCY + formatPrice(cartTotalPrice)}
              </span>
            </h4>
          </div>
          <CtaLink to={urls.cart} block className={"mb-3"}>
            {t("view_cart")}
          </CtaLink>
          <CtaLink to={urls.checkout} block className={"mb-2"}>
            {t("checkout")}
          </CtaLink>
        </Fragment>
      ) : (
        <p className="text-center">{t("cart_empty")}</p>
      )}
    </div>
  );
};

MenuCart.propTypes = {
  cartData: PropTypes.array,
  deleteFromCart: PropTypes.func,
};

export default MenuCart;
